import type { ClassDTO } from '@shared/protocol/messages';
import type { App } from '../app/App';
import { cardToSend, classPrizeReady, contestRound, syncClass } from '../game/systems/classroom';
import { t } from '../i18n';

/** Fetches my class board and remembers whether I am in a class. Undefined when offline. */
export async function refreshClass(app: App): Promise<ClassDTO | null | undefined> {
  const online = app.store.state.online;
  if (!online) return undefined;
  app.api.token = online.token;
  try {
    const cls = await app.api.myClass();
    keepClass(app, cls);
    return cls;
  } catch {
    return undefined;
  }
}

/** Remembers the class I am in after a class action. */
export function keepClass(app: App, cls: ClassDTO | null): void {
  if (app.store.state.community.classId !== (cls?.id ?? null)) app.store.update([], (s) => void syncClass(s, cls));
}

/** Weeks whose "goal reached" toast was already shown in this session. */
const announced = new Set<string>();

/**
 * Counts a finished battle towards my class's weekly goal (quiet when
 * signed out, not in a class or offline) and says so when it completes the goal.
 */
export async function reportClassBattle(app: App): Promise<void> {
  const save = app.store.state;
  if (!save.online || !save.community.classId) return;
  app.api.token = save.online.token;
  try {
    const res = await app.api.classBattle(contestRound());
    if (!res.goal) {
      keepClass(app, null); // left (or was taken out of) the class on another device
      return;
    }
    if (classPrizeReady(app.store.state, res.goal) && !announced.has(res.goal.round)) {
      announced.add(res.goal.round);
      app.toast(t('Your class reached its goal! Claim your coins in Notebook → Friends → My Class.'), 'success');
    }
  } catch {
    // Offline or a week change: the class goal simply misses this battle.
  }
}

/** Sends friends my notebook card when it changed since they last got it. Quiet on failure. */
export async function publishNotebookCard(app: App): Promise<void> {
  const online = app.store.state.online;
  const card = cardToSend(app.store.state);
  if (!online || !card) return;
  app.api.token = online.token;
  try {
    await app.api.putNotebookCard(card);
    app.store.update([], (s) => (s.community.cardSent = JSON.stringify(card)));
  } catch {
    // Try again next time the Friends page opens.
  }
}
